import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const LabConfigurator = () => {
  const [configuration, setConfiguration] = useState({
    labType: '',
    studentCapacity: '',
    duration: '',
    features: [],
    integrations: []
  });

  const [pricing, setPricing] = useState(null);

  const labTypes = [
    { value: 'ai-fundamentals', label: 'AI Fundamentals Lab' },
    { value: 'machine-learning', label: 'Machine Learning Lab' },
    { value: 'robotics-engineering', label: 'Robotics Engineering Lab' },
    { value: 'data-science', label: 'Data Science Lab' },
    { value: 'computer-vision', label: 'Computer Vision Lab' },
    { value: 'nlp-processing', label: 'Natural Language Processing Lab' }
  ];

  const capacities = [
    { value: '10-25', label: '10-25 students' },
    { value: '25-50', label: '25-50 students' },
    { value: '50-100', label: '50-100 students' },
    { value: '100-200', label: '100-200 students' },
    { value: '200+', label: '200+ students' }
  ];

  const durations = [
    { value: '1-semester', label: '1 Semester' },
    { value: '2-semester', label: '2 Semesters' },
    { value: '1-year', label: '1 Academic Year' },
    { value: '2-year', label: '2 Academic Years' },
    { value: 'ongoing', label: 'Ongoing Program' }
  ];

  const availableFeatures = [
    { id: 'cloud-computing', label: 'Cloud Computing Resources', price: 500 },
    { id: 'gpu-acceleration', label: 'GPU Acceleration', price: 800 },
    { id: 'collaborative-tools', label: 'Collaborative Development Tools', price: 300 },
    { id: 'assessment-system', label: 'Automated Assessment System', price: 400 },
    { id: 'project-templates', label: 'Industry Project Templates', price: 200 },
    { id: 'mentor-support', label: '24/7 Mentor Support', price: 600 },
    { id: 'certification', label: 'Industry Certification', price: 350 },
    { id: 'analytics-dashboard', label: 'Learning Analytics Dashboard', price: 450 }
  ];

  const availableIntegrations = [
    { id: 'lms-integration', label: 'LMS Integration (Canvas, Blackboard)', price: 300 },
    { id: 'github-enterprise', label: 'GitHub Enterprise', price: 200 },
    { id: 'jupyter-notebooks', label: 'Jupyter Notebooks', price: 150 },
    { id: 'docker-containers', label: 'Docker Containers', price: 250 },
    { id: 'aws-integration', label: 'AWS Integration', price: 400 },
    { id: 'azure-integration', label: 'Azure Integration', price: 400 },
    { id: 'google-cloud', label: 'Google Cloud Platform', price: 400 }
  ];

  const handleFeatureChange = (featureId, checked) => {
    setConfiguration(prev => ({
      ...prev,
      features: checked 
        ? [...prev.features, featureId]
        : prev.features.filter(id => id !== featureId)
    }));
  };

  const handleIntegrationChange = (integrationId, checked) => {
    setConfiguration(prev => ({
      ...prev,
      integrations: checked 
        ? [...prev.integrations, integrationId]
        : prev.integrations.filter(id => id !== integrationId)
    }));
  };

  const calculatePricing = () => {
    const basePrices = {
      'ai-fundamentals': 2000,
      'machine-learning': 2500,
      'robotics-engineering': 3000,
      'data-science': 2200,
      'computer-vision': 2800,
      'nlp-processing': 2600
    };

    const capacityMultipliers = {
      '10-25': 1.0,
      '25-50': 1.5,
      '50-100': 2.0,
      '100-200': 3.0,
      '200+': 4.0
    };

    const durationMultipliers = {
      '1-semester': 1.0,
      '2-semester': 1.8,
      '1-year': 3.0,
      '2-year': 5.5,
      'ongoing': 8.0
    };

    const basePrice = basePrices[configuration.labType] || 2000;
    const capacityMultiplier = capacityMultipliers[configuration.studentCapacity] || 1.0;
    const durationMultiplier = durationMultipliers[configuration.duration] || 1.0;

    const featuresPrice = configuration.features.reduce((total, featureId) => {
      const feature = availableFeatures.find(f => f.id === featureId);
      return total + (feature ? feature.price : 0);
    }, 0);

    const integrationsPrice = configuration.integrations.reduce((total, integrationId) => {
      const integration = availableIntegrations.find(i => i.id === integrationId);
      return total + (integration ? integration.price : 0);
    }, 0);

    const totalPrice = Math.round((basePrice * capacityMultiplier * durationMultiplier) + featuresPrice + integrationsPrice);
    const monthlyPrice = Math.round(totalPrice / (durationMultiplier * 4));

    setPricing({
      total: totalPrice,
      monthly: monthlyPrice,
      setup: Math.round(totalPrice * 0.1),
      support: Math.round(totalPrice * 0.15)
    });
  };

  const isConfigurationComplete = configuration.labType && configuration.studentCapacity && configuration.duration;

  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-foreground mb-4">
            Configure Your
            <span className="gradient-text"> Virtual Lab</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Build a customized AI/Robotics learning environment tailored to your curriculum needs and student capacity
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Configuration Panel */}
          <div className="lg:col-span-2 space-y-8">
            {/* Basic Configuration */}
            <div className="holographic-card p-8 bg-card">
              <div className="flex items-center space-x-3 mb-6">
                <Icon name="Settings" size={24} className="text-accent" />
                <h3 className="text-xl font-bold text-foreground">Basic Configuration</h3>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <Select
                  label="Lab Type"
                  options={labTypes}
                  value={configuration.labType}
                  onChange={(value) => setConfiguration(prev => ({ ...prev, labType: value }))}
                  placeholder="Select lab type"
                  required
                />

                <Select
                  label="Student Capacity"
                  options={capacities}
                  value={configuration.studentCapacity}
                  onChange={(value) => setConfiguration(prev => ({ ...prev, studentCapacity: value }))}
                  placeholder="Select capacity"
                  required
                />

                <Select
                  label="Program Duration"
                  options={durations}
                  value={configuration.duration}
                  onChange={(value) => setConfiguration(prev => ({ ...prev, duration: value }))}
                  placeholder="Select duration"
                  required
                  className="md:col-span-2"
                />
              </div>
            </div>

            {/* Features Selection */}
            <div className="holographic-card p-8 bg-card">
              <div className="flex items-center space-x-3 mb-6">
                <Icon name="Zap" size={24} className="text-accent" />
                <h3 className="text-xl font-bold text-foreground">Advanced Features</h3>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {availableFeatures.map((feature) => (
                  <div key={feature.id} className="flex items-center justify-between p-4 bg-background/50 rounded-lg border border-border">
                    <div className="flex items-center space-x-3">
                      <Checkbox
                        checked={configuration.features.includes(feature.id)}
                        onChange={(e) => handleFeatureChange(feature.id, e.target.checked)}
                      />
                      <span className="text-foreground">{feature.label}</span>
                    </div>
                    <span className="text-sm text-accent font-medium">+${feature.price}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Integrations */}
            <div className="holographic-card p-8 bg-card">
              <div className="flex items-center space-x-3 mb-6">
                <Icon name="Plug" size={24} className="text-accent" />
                <h3 className="text-xl font-bold text-foreground">System Integrations</h3>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {availableIntegrations.map((integration) => (
                  <div key={integration.id} className="flex items-center justify-between p-4 bg-background/50 rounded-lg border border-border">
                    <div className="flex items-center space-x-3">
                      <Checkbox
                        checked={configuration.integrations.includes(integration.id)}
                        onChange={(e) => handleIntegrationChange(integration.id, e.target.checked)}
                      />
                      <span className="text-foreground">{integration.label}</span>
                    </div>
                    <span className="text-sm text-accent font-medium">+${integration.price}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Pricing Panel */}
          <div className="space-y-6">
            <div className="holographic-card p-6 bg-card sticky top-8">
              <div className="flex items-center space-x-3 mb-6">
                <Icon name="DollarSign" size={24} className="text-success" />
                <h3 className="text-xl font-bold text-foreground">Pricing Summary</h3>
              </div>

              {!pricing ? (
                <div className="text-center space-y-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-accent/20 to-cyber-purple/20 rounded-full flex items-center justify-center mx-auto">
                    <Icon name="Calculator" size={32} className="text-accent" />
                  </div>
                  <p className="text-muted-foreground">
                    Complete the configuration to see pricing
                  </p>
                  <Button
                    variant="default"
                    size="lg"
                    fullWidth
                    className="morphing-cta text-white font-semibold"
                    onClick={calculatePricing}
                    disabled={!isConfigurationComplete}
                    iconName="Calculator"
                    iconPosition="left"
                  >
                    Calculate Pricing
                  </Button>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Total Price */}
                  <div className="text-center p-6 bg-gradient-to-br from-accent/10 to-cyber-purple/10 rounded-lg border border-accent/20">
                    <div className="text-3xl font-bold text-accent mb-2">
                      ${pricing.total.toLocaleString()}
                    </div>
                    <div className="text-sm text-muted-foreground">Total Program Cost</div>
                    <div className="text-lg font-semibold text-foreground mt-2">
                      ${pricing.monthly.toLocaleString()}/month
                    </div>
                  </div>

                  {/* Breakdown */}
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b border-border">
                      <span className="text-muted-foreground">Setup Fee</span>
                      <span className="font-semibold text-foreground">${pricing.setup.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-border">
                      <span className="text-muted-foreground">Support & Maintenance</span>
                      <span className="font-semibold text-foreground">${pricing.support.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-muted-foreground">Features & Integrations</span>
                      <span className="font-semibold text-foreground">
                        ${(pricing.total - pricing.setup - pricing.support).toLocaleString()}
                      </span>
                    </div>
                  </div>

                  {/* CTAs */}
                  <div className="space-y-3">
                    <Button
                      variant="default"
                      size="lg"
                      fullWidth
                      className="morphing-cta text-white font-semibold"
                      iconName="Calendar"
                      iconPosition="left"
                    >
                      Request Demo
                    </Button>
                    
                    <Button
                      variant="outline"
                      size="lg"
                      fullWidth
                      iconName="Download"
                      iconPosition="left"
                    >
                      Download Quote
                    </Button>
                  </div>

                  {/* Guarantee */}
                  <div className="text-center p-4 bg-success/10 rounded-lg border border-success/20">
                    <Icon name="Shield" size={20} className="text-success mx-auto mb-2" />
                    <div className="text-sm text-success font-medium">30-Day Money Back Guarantee</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LabConfigurator;